import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Preguntas Frecuentes - Instalación de Pladur Barcelona | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Resuelve tus dudas sobre instalación de pladur en Barcelona: precios, plazos, aislamientos, acabados y más. Respuestas claras de profesionales.');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://barcelona.pladures.es';

    // Add FAQPage Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "¿Cuánto cuesta la instalación de pladur en Barcelona?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El precio varía según el tipo de trabajo, los m², la altura, el tipo de placa y el nivel de acabado. Un tabique estándar puede costar entre 25-40€/m², mientras que un falso techo entre 30-50€/m². Los trasdosados con aislamiento rondan los 35-55€/m². Te ofrecemos un presupuesto detallado y personalizado sin compromiso tras evaluar tu proyecto."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cuánto tiempo tarda la instalación de pladur?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Depende del proyecto. Un tabique de una habitación estándar se puede completar en 1-2 días. Un falso techo de 20-30m² suele tardar 2-3 días. Una reforma completa de un piso puede llevar 1-2 semanas. En el presupuesto te indicamos el plazo exacto según tu proyecto específico."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué opciones de aislamiento acústico ofrecéis?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ofrecemos varias soluciones: lana mineral de alta densidad (la más efectiva), sistemas de doble placa con cámara de aire, bandas resilientes antivibratorias y placas acústicas especiales. Según el nivel de aislamiento que necesites (reducción de 40-60 dB), recomendamos la combinación más adecuada. Es especialmente efectivo para dormitorios, salas de música y oficinas."
          }
        },
        {
          "@type": "Question",
          "name": "¿El pladur sirve para zonas húmedas como baños?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, utilizamos placas hidrófugas (de color verde) específicamente diseñadas para zonas con humedad. Son resistentes al agua y perfectas para baños, cocinas y lavaderos. Estas placas llevan un tratamiento especial que las hace aptas para alicatar encima. No recomendamos pladur estándar en zonas húmedas."
          }
        },
        {
          "@type": "Question",
          "name": "¿Puedo colgar muebles pesados en tabiques de pladur?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, pero requiere refuerzos. Para elementos pesados como muebles de cocina, televisores grandes o estanterías, instalamos refuerzos de madera o perfiles metálicos dentro del tabique durante la construcción. Con los refuerzos adecuados, puedes colgar elementos de hasta 50-80 kg sin problema. Es importante planificarlo antes de la instalación."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué mejora aporta el aislamiento térmico?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El aislamiento térmico con pladur reduce significativamente las pérdidas de calor en invierno y la entrada de calor en verano. Esto se traduce en ahorro en calefacción y aire acondicionado (hasta 30-40% menos consumo), mayor confort térmico y cumplimiento de normativas de eficiencia energética. Usamos lana mineral o poliestireno según las necesidades."
          }
        },
        {
          "@type": "Question",
          "name": "¿Trabajáis en viviendas u oficinas ocupadas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, trabajamos habitualmente en espacios ocupados. Coordinamos horarios para minimizar molestias, mantenemos el área de trabajo limpia y ordenada, protegemos muebles y suelos, y recogemos al final de cada jornada. Muchos clientes continúan con su rutina normal mientras realizamos la instalación de pladur."
          }
        },
        {
          "@type": "Question",
          "name": "¿El pladur queda listo para pintar directamente?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, dejamos las superficies completamente lisas y preparadas para pintar. Realizamos el tratamiento de juntas con cinta y pasta, lijamos y aplicamos una imprimación si es necesario. Solo necesitas aplicar la pintura del color que prefieras. Si lo deseas, también podemos encargarnos del pintado."
          }
        },
        {
          "@type": "Question",
          "name": "¿Se necesitan permisos para instalar pladur?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Para trabajos interiores en viviendas (tabiques, trasdosados, falsos techos) generalmente no se requieren permisos especiales si no afectas a elementos estructurales. Para locales comerciales u obras mayores, puede ser necesaria una licencia de obra menor. Te asesoramos según tu caso específico y, si es necesario, te ayudamos con la gestión."
          }
        },
        {
          "@type": "Question",
          "name": "¿En qué horario trabajáis?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nuestro horario habitual es de lunes a viernes de 08:00 a 18:00. Podemos adaptarnos a tus necesidades si requieres horarios específicos (por ejemplo, trabajar solo por las mañanas o evitar ciertas horas). Lo coordinamos contigo antes de empezar para que sea lo más cómodo posible."
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    // Add WebPage Schema
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Preguntas Frecuentes - Instalación de Pladur Barcelona",
      "description": "Resuelve tus dudas sobre instalación de pladur en Barcelona: precios, plazos, aislamientos, acabados y más.",
      "url": `${siteUrl}/faq`,
      "inLanguage": "es-ES",
      "isPartOf": {
        "@type": "WebSite",
        "name": "PLADUREX Barcelona",
        "url": siteUrl
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${siteUrl}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Preguntas Frecuentes",
            "item": `${siteUrl}/faq`
          }
        ]
      }
    };

    const webPageScript = document.createElement('script');
    webPageScript.type = 'application/ld+json';
    webPageScript.text = JSON.stringify(webPageSchema);
    document.head.appendChild(webPageScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(webPageScript);
    };
  }, []);

  const faqs = [
    {
      question: '¿Cuánto cuesta la instalación de pladur en Barcelona?',
      answer: 'El precio varía según el tipo de trabajo, los m², la altura, el tipo de placa y el nivel de acabado. Un tabique estándar puede costar entre 25-40€/m², mientras que un falso techo entre 30-50€/m². Los trasdosados con aislamiento rondan los 35-55€/m². Te ofrecemos un presupuesto detallado y personalizado sin compromiso tras evaluar tu proyecto.',
    },
    {
      question: '¿Cuánto tiempo tarda la instalación de pladur?',
      answer: 'Depende del proyecto. Un tabique de una habitación estándar se puede completar en 1-2 días. Un falso techo de 20-30m² suele tardar 2-3 días. Una reforma completa de un piso puede llevar 1-2 semanas. En el presupuesto te indicamos el plazo exacto según tu proyecto específico.',
    },
    {
      question: '¿Qué opciones de aislamiento acústico ofrecéis?',
      answer: 'Ofrecemos varias soluciones: lana mineral de alta densidad (la más efectiva), sistemas de doble placa con cámara de aire, bandas resilientes antivibratorias y placas acústicas especiales. Según el nivel de aislamiento que necesites (reducción de 40-60 dB), recomendamos la combinación más adecuada. Es especialmente efectivo para dormitorios, salas de música y oficinas.',
    },
    {
      question: '¿El pladur sirve para zonas húmedas como baños?',
      answer: 'Sí, utilizamos placas hidrófugas (de color verde) específicamente diseñadas para zonas con humedad. Son resistentes al agua y perfectas para baños, cocinas y lavaderos. Estas placas llevan un tratamiento especial que las hace aptas para alicatar encima. No recomendamos pladur estándar en zonas húmedas.',
    },
    {
      question: '¿Puedo colgar muebles pesados en tabiques de pladur?',
      answer: 'Sí, pero requiere refuerzos. Para elementos pesados como muebles de cocina, televisores grandes o estanterías, instalamos refuerzos de madera o perfiles metálicos dentro del tabique durante la construcción. Con los refuerzos adecuados, puedes colgar elementos de hasta 50-80 kg sin problema. Es importante planificarlo antes de la instalación.',
    },
    {
      question: '¿Qué mejora aporta el aislamiento térmico?',
      answer: 'El aislamiento térmico con pladur reduce significativamente las pérdidas de calor en invierno y la entrada de calor en verano. Esto se traduce en ahorro en calefacción y aire acondicionado (hasta 30-40% menos consumo), mayor confort térmico y cumplimiento de normativas de eficiencia energética. Usamos lana mineral o poliestireno según las necesidades.',
    },
    {
      question: '¿Trabajáis en viviendas u oficinas ocupadas?',
      answer: 'Sí, trabajamos habitualmente en espacios ocupados. Coordinamos horarios para minimizar molestias, mantenemos el área de trabajo limpia y ordenada, protegemos muebles y suelos, y recogemos al final de cada jornada. Muchos clientes continúan con su rutina normal mientras realizamos la instalación de pladur.',
    },
    {
      question: '¿El pladur queda listo para pintar directamente?',
      answer: 'Sí, dejamos las superficies completamente lisas y preparadas para pintar. Realizamos el tratamiento de juntas con cinta y pasta, lijamos y aplicamos una imprimación si es necesario. Solo necesitas aplicar la pintura del color que prefieras. Si lo deseas, también podemos encargarnos del pintado.',
    },
    {
      question: '¿Se necesitan permisos para instalar pladur?',
      answer: 'Para trabajos interiores en viviendas (tabiques, trasdosados, falsos techos) generalmente no se requieren permisos especiales si no afectas a elementos estructurales. Para locales comerciales u obras mayores, puede ser necesaria una licencia de obra menor. Te asesoramos según tu caso específico y, si es necesario, te ayudamos con la gestión.',
    },
    {
      question: '¿En qué horario trabajáis?',
      answer: 'Nuestro horario habitual es de lunes a viernes de 08:00 a 18:00. Podemos adaptarnos a tus necesidades si requieres horarios específicos (por ejemplo, trabajar solo por las mañanas o evitar ciertas horas). Lo coordinamos contigo antes de empezar para que sea lo más cómodo posible.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 bg-gradient-to-br from-primary-dark to-primary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/1c4e3d90ddc00bef772f1095b2cb259f.jpg"
              alt="Preguntas frecuentes"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-white font-extrabold text-3xl sm:text-5xl md:text-6xl mb-6"
            >
              Preguntas frecuentes sobre pladur
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-white/90 text-xl leading-relaxed max-w-3xl mx-auto"
            >
              Resolvemos todas tus dudas sobre la <Link to="/servicios" className="text-white underline hover:text-white/80">instalación de pladur en Barcelona</Link>: precios, plazos, materiales y procesos.
            </motion.p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-bg-light">
          <div className="max-w-4xl mx-auto px-6">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-300"
                  >
                    <h3 className="text-primary-dark text-lg font-bold pr-4">
                      {faq.question}
                    </h3>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-blue-200 flex items-center justify-center">
                      <motion.i
                        animate={{ rotate: openIndex === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-primary text-xl`}
                      ></motion.i>
                    </div>
                  </button>
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-8 pb-6">
                          <p className="text-gray-600 text-[15px] leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <p className="text-gray-600 mb-4">
                Descubre todos nuestros <Link to="/servicios" className="text-primary hover:underline font-semibold">servicios de pladur</Link> o conoce más <Link to="/empresa" className="text-primary hover:underline font-semibold">sobre nuestra empresa</Link>.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-primary-dark text-4xl md:text-5xl font-serif font-bold mb-6">
                ¿No encuentras tu respuesta?
              </h2>
              <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                Estamos aquí para ayudarte. Contacta con nosotros y resolveremos todas tus dudas sobre tu proyecto de <Link to="/servicios" className="text-primary hover:underline font-semibold">instalación de pladur en Barcelona</Link>.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <a
                  href="tel:+34629501941"
                  className="bg-primary text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-primary-dark transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  <span>+34 629 501 941</span>
                </a>
                <Link
                  to="/contacto"
                  className="border-2 border-primary text-primary px-8 py-4 rounded-lg font-bold text-lg hover:bg-primary hover:text-white transition-all duration-300 whitespace-nowrap"
                >
                  Formulario de contacto
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default FaqPage;
